import Link from "next/link";
import SEOConfig from "@/common/seo.config";
import { Footer, Header, SEO } from "@/components";
import {
  LandingSection,
  PageHero,
} from "@/components/main/landing/landing-ui";
import {
  buildBreadcrumbList,
  buildWebPage,
  pageSchemas,
} from "@/lib/schema";

const SECTIONS = [
  {
    title: "Who we are",
    body: "Nivalabs AI (a PySquad company) operates this website. This notice explains how we handle information collected through nivalabs.ai and related contact forms.",
  },
  {
    title: "Information we collect",
    body: "When you contact us, we collect the details you provide (such as name, email, company, and message content). We may also collect standard technical logs (IP address, browser type, pages viewed) needed to operate and secure the site.",
  },
  {
    title: "How we use information",
    body: "We use contact details to respond to inquiries, schedule discovery conversations, and deliver services you request. Technical logs help us maintain reliability, diagnose issues, and protect against abuse.",
  },
  {
    title: "Sharing",
    body: "We do not sell personal information. We may share data with processors that help us run email, hosting, analytics, or scheduling, under agreements that limit use to providing those services. We may disclose information if required by law.",
  },
  {
    title: "Retention",
    body: "We keep inquiry records as long as needed to manage the relationship and meet legal or operational requirements, then delete or anonymize them when no longer needed.",
  },
  {
    title: "Your choices",
    body: "You may request access, correction, or deletion of personal information we hold about you by contacting us. We will respond within a reasonable period subject to applicable law.",
  },
  {
    title: "Contact",
    body: "For privacy questions, use the contact form on this site or email the address listed on the Contact page.",
  },
];

export default function PrivacyPage() {
  const structuredData = pageSchemas([
    buildWebPage({
      path: "/privacy",
      name: SEOConfig.privacy.pageTitle,
      description: SEOConfig.privacy.pageDescription,
    }),
    buildBreadcrumbList([
      { name: "Home", path: "/" },
      { name: "Privacy", path: "/privacy" },
    ]),
  ]);

  return (
    <>
      <SEO {...SEOConfig.privacy} structuredData={structuredData} />
      <Header />
      <main>
        <PageHero
          compact
          title="Privacy"
          lead="How Nivalabs handles information collected through this website and contact channels."
        />

        <LandingSection narrow>
          <p className="text-sm text-ink-soft">Last updated: July 30, 2026</p>
          <div className="mt-10 space-y-10">
            {SECTIONS.map((section) => (
              <div key={section.title}>
                <h2 className="text-xl font-semibold text-ink">{section.title}</h2>
                <p className="mt-3 text-base leading-relaxed text-ink-muted">
                  {section.body}
                </p>
              </div>
            ))}
          </div>
          <p className="mt-12 text-sm text-ink-muted">
            Related:{" "}
            <Link
              href="/terms"
              className="font-semibold text-[#1c8c80] no-underline hover:underline"
            >
              Terms of use
            </Link>
            {" · "}
            <Link
              href="/contact"
              className="font-semibold text-[#1c8c80] no-underline hover:underline"
            >
              Contact
            </Link>
          </p>
        </LandingSection>
      </main>
      <Footer />
    </>
  );
}
